/**
 * ReviewStage — LEFT column while stage === REVIEW.
 *
 * The operator refines the reviewed content: primary hook + alternates,
 * script body, primary CTA + alternates, creative brief and production
 * instructions. Every edit autosaves (debounced). Nothing regenerates and
 * editing a field NEVER triggers a chat/LLM call. Submit routes exactly the
 * reviewed content after an explicit confirmation.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAutosave, SaveIndicator, AutosaveTextarea } from './useCcsApi';

export default function ReviewStage({
  session,
  api,
  applySession,
  refreshSession,
  setCopyHandler,
}) {
  const id = session.session_id;
  const artifacts = session.artifacts || {};
  const hooks = artifacts.hooks || [];
  const ctas = artifacts.ctas || [];
  const bodyArtifact = artifacts.body || null;
  const brief = session.brief || {};

  const primaryHook = hooks.find((h) => h.role === 'primary') || hooks[0] || null;
  const altHooks = hooks.filter((h) => h !== primaryHook);
  const primaryCta = ctas.find((c) => c.role === 'primary') || ctas[0] || null;
  const altCtas = ctas.filter((c) => c !== primaryCta);

  // ---- Primary-field local state (copy-to-field targets) ----
  const [pHook, setPHook] = useState(primaryHook?.text || '');
  const [bodyText, setBodyText] = useState(bodyArtifact?.text || '');
  const [pCta, setPCta] = useState(primaryCta?.text || '');

  // Re-sync only when the underlying artifact identity changes (not on every
  // session refresh) so in-progress local edits are never clobbered.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- resync only on artifact identity change, never on text edits
  useEffect(() => { setPHook(primaryHook?.text || ''); }, [primaryHook?.artifact_id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setBodyText(bodyArtifact?.text || ''); }, [bodyArtifact?.artifact_id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPCta(primaryCta?.text || ''); }, [primaryCta?.artifact_id]);

  const hookSave = useAutosave(
    useCallback((t) => (primaryHook ? api.updateArtifact(id, primaryHook.artifact_id, t) : Promise.resolve()), [api, id, primaryHook]),
  );
  const bodySave = useAutosave(
    useCallback((t) => (bodyArtifact ? api.updateArtifact(id, bodyArtifact.artifact_id, t) : Promise.resolve()), [api, id, bodyArtifact]),
  );
  const ctaSave = useAutosave(
    useCallback((t) => (primaryCta ? api.updateArtifact(id, primaryCta.artifact_id, t) : Promise.resolve()), [api, id, primaryCta]),
  );

  // ---- Copy-to-field ----
  const [copyConflict, setCopyConflict] = useState(null); // { key, text }

  const applyCopy = useCallback((key, text, mode) => {
    if (key === 'primary_hook') {
      const next = mode === 'append' && pHook ? `${pHook}\n${text}` : text;
      setPHook(next); hookSave.schedule(next);
    } else if (key === 'body') {
      const next = mode === 'append' && bodyText ? `${bodyText}\n${text}` : text;
      setBodyText(next); bodySave.schedule(next);
    } else if (key === 'primary_cta') {
      const next = mode === 'append' && pCta ? `${pCta}\n${text}` : text;
      setPCta(next); ctaSave.schedule(next);
    }
  }, [pHook, bodyText, pCta, hookSave, bodySave, ctaSave]);

  const requestCopy = useCallback((key, text) => {
    const currents = { primary_hook: pHook, body: bodyText, primary_cta: pCta };
    if ((currents[key] || '').trim()) setCopyConflict({ key, text });
    else applyCopy(key, text, 'replace');
  }, [pHook, bodyText, pCta, applyCopy]);

  useEffect(() => {
    setCopyHandler(requestCopy);
    return () => setCopyHandler(null);
  }, [requestCopy, setCopyHandler]);

  // ---- Structural mutations (add / remove alternates) ----
  const [busy, setBusy] = useState(false);
  const [undo, setUndo] = useState(null); // { kind, text }

  const addAlternate = async (kind) => {
    setBusy(true);
    try { applySession(await api.addArtifact(id, kind, '')); }
    finally { setBusy(false); }
  };

  const removeAlternate = async (kind, artifact) => {
    setBusy(true);
    try {
      applySession(await api.deleteArtifact(id, artifact.artifact_id));
      setUndo({ kind, text: artifact.text || '' });
    } finally { setBusy(false); }
  };

  const doUndo = async () => {
    if (!undo) return;
    setBusy(true);
    try { applySession(await api.addArtifact(id, undo.kind, undo.text)); setUndo(null); }
    finally { setBusy(false); }
  };

  // ---- Back ----
  const [navBusy, setNavBusy] = useState(false);
  const handleBack = async () => {
    hookSave.flush(); bodySave.flush(); ctaSave.flush();
    setNavBusy(true);
    try { applySession(await api.back(id)); }
    finally { setNavBusy(false); }
  };

  // ---- Submit ----
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const openConfirm = async () => {
    hookSave.flush(); bodySave.flush(); ctaSave.flush();
    setSubmitError(null);
    // pull the freshest saved alternates so the summary is accurate
    try { await refreshSession(); } catch (e) { /* summary falls back to current */ }
    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await api.submit(id);
      const s = res.session || res.result?.session || null;
      const resultErr = res.result?.error || res.result?.last_error;
      const submissionErr = s?.submission?.last_error;
      const succeeded = s?.stage === 'SUBMITTED' && !resultErr;
      if (succeeded) {
        applySession(s);
        setConfirmOpen(false);
      } else {
        if (s) applySession(s);
        const msg = resultErr || submissionErr || 'Submission failed. Your content is intact — please retry.';
        setSubmitError(typeof msg === 'string' ? msg : 'Submission failed. Please retry.');
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setSubmitError((typeof detail === 'string' && detail) || 'Submission failed. Your content is intact — please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  const conflictLabel = copyConflict
    ? ({ primary_hook: 'Primary Hook', body: 'Script Body', primary_cta: 'Primary CTA' })[copyConflict.key]
    : '';

  const videoFormat = session.draft?.video_format || '—';

  return (
    <div className="space-y-4" data-testid="ccs-review-stage">
      {/* Hooks */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">Hooks</h3>
          <SaveIndicator status={hookSave.status} onRetry={hookSave.retry} />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-400">Primary Hook</label>
          <textarea
            value={pHook}
            onChange={(e) => { setPHook(e.target.value); hookSave.schedule(e.target.value); }}
            onBlur={hookSave.flush}
            rows={2}
            placeholder="Primary hook…"
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none resize-y"
            data-testid="ccs-primary-hook"
          />
        </div>

        {altHooks.map((h) => (
          <div key={h.artifact_id} className="pl-3 border-l-2 border-slate-700/60">
            <AutosaveTextarea
              key={h.artifact_id}
              initialValue={h.text || ''}
              saveFn={(t) => api.updateArtifact(id, h.artifact_id, t)}
              label="Alternate hook"
              placeholder="Alternate hook…"
              rows={2}
              testId={`ccs-alt-hook-${h.artifact_id}`}
              headerRight={(
                <button
                  type="button"
                  onClick={() => removeAlternate('hook', h)}
                  disabled={busy}
                  className="text-xs text-rose-400 hover:text-rose-300 disabled:opacity-40"
                  data-testid={`ccs-remove-hook-${h.artifact_id}`}
                >
                  Remove
                </button>
              )}
            />
          </div>
        ))}

        <button
          type="button"
          onClick={() => addAlternate('hook')}
          disabled={busy}
          className="text-xs text-emerald-400 hover:text-emerald-300 disabled:opacity-40"
          data-testid="ccs-add-hook"
        >
          + Add alternate hook
        </button>
      </div>

      {/* Script Body */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">Script Body</h3>
          <SaveIndicator status={bodySave.status} onRetry={bodySave.retry} />
        </div>
        {bodyArtifact ? (
          <textarea
            value={bodyText}
            onChange={(e) => { setBodyText(e.target.value); bodySave.schedule(e.target.value); }}
            onBlur={bodySave.flush}
            rows={8}
            placeholder="Script body…"
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none resize-y"
            data-testid="ccs-body"
          />
        ) : (
          <p className="text-xs text-slate-500">No script body was carried into review.</p>
        )}
      </div>

      {/* CTAs */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">CTAs</h3>
          <SaveIndicator status={ctaSave.status} onRetry={ctaSave.retry} />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-400">Primary CTA</label>
          <textarea
            value={pCta}
            onChange={(e) => { setPCta(e.target.value); ctaSave.schedule(e.target.value); }}
            onBlur={ctaSave.flush}
            rows={2}
            placeholder="Primary CTA…"
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none resize-y"
            data-testid="ccs-primary-cta"
          />
        </div>

        {altCtas.map((c) => (
          <div key={c.artifact_id} className="pl-3 border-l-2 border-slate-700/60">
            <AutosaveTextarea
              key={c.artifact_id}
              initialValue={c.text || ''}
              saveFn={(t) => api.updateArtifact(id, c.artifact_id, t)}
              label="Alternate CTA"
              placeholder="Alternate CTA…"
              rows={2}
              testId={`ccs-alt-cta-${c.artifact_id}`}
              headerRight={(
                <button
                  type="button"
                  onClick={() => removeAlternate('cta', c)}
                  disabled={busy}
                  className="text-xs text-rose-400 hover:text-rose-300 disabled:opacity-40"
                  data-testid={`ccs-remove-cta-${c.artifact_id}`}
                >
                  Remove
                </button>
              )}
            />
          </div>
        ))}

        <button
          type="button"
          onClick={() => addAlternate('cta')}
          disabled={busy}
          className="text-xs text-emerald-400 hover:text-emerald-300 disabled:opacity-40"
          data-testid="ccs-add-cta"
        >
          + Add alternate CTA
        </button>
      </div>

      {/* Undo bar */}
      {undo && (
        <div className="flex items-center gap-3 text-xs text-slate-400 bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2" data-testid="ccs-undo-bar">
          Removed an alternate {undo.kind}.
          <button
            type="button"
            onClick={doUndo}
            disabled={busy}
            className="underline text-emerald-400 hover:text-emerald-300 disabled:opacity-40"
            data-testid="ccs-undo-btn"
          >
            Undo
          </button>
          <button
            type="button"
            onClick={() => setUndo(null)}
            className="ml-auto text-slate-500 hover:text-slate-300"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── The naming block. SYSTEM-ASSIGNED, READ-ONLY. ──────────────────
          Founder, 2026-09-08: "This is a system-assigned value specifically to
          remove human error — nobody should be able to override it by hand."

          It used to be the opening lines of `brief_text`, inside the textarea
          below — so the filename an editor is told to use "EXACTLY" was itself
          editable, and whatever was typed went to Trello verbatim. It is now a
          separate stored field, rendered here as text with no input around it,
          and re-attached at routing time from the stored value. There is no
          control to change it because there is no control at all. */}
      {brief.naming_block ? (
        <div
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-4"
          data-testid="ccs-naming-block"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-400">
              Naming — assigned by Cipher
            </span>
            <span className="text-[11px] text-slate-500" data-testid="ccs-naming-locked">
              Not editable
            </span>
          </div>
          <pre className="text-xs text-slate-200 whitespace-pre-wrap font-mono leading-relaxed">
            {brief.naming_block}
          </pre>
        </div>
      ) : (
        // Rule 10: a missing name is stated, not left as a blank space that
        // reads as "there is nothing to say here".
        <div
          className="bg-slate-900/50 border border-amber-800/50 rounded-xl p-4"
          data-testid="ccs-naming-missing"
        >
          <p className="text-xs text-amber-400">
            Cipher has not assigned a name to this creative yet. Do not invent
            one — tell the Cipher operator before this is produced.
          </p>
        </div>
      )}

      {/* Creative Brief */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
        <AutosaveTextarea
          initialValue={brief.brief_text || ''}
          saveFn={(t) => api.saveBrief(id, { brief_text: t })}
          label="Creative Brief"
          placeholder="Creative brief…"
          rows={4}
          testId="ccs-brief"
        />
      </div>

      {/* Production Instructions */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
        <AutosaveTextarea
          initialValue={brief.production_instructions || ''}
          saveFn={(t) => api.saveBrief(id, { production_instructions: t })}
          label="Production Instructions"
          placeholder="Write the production notes for this piece. Ask Claude on the right if you want help drafting them."
          rows={3}
          testId="ccs-production"
        />
      </div>

      {submitError && (
        <p className="text-xs text-rose-400" data-testid="ccs-submit-error">{submitError}</p>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={handleBack}
          disabled={navBusy}
          className="px-4 py-2 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 transition-colors text-sm disabled:opacity-40"
          data-testid="ccs-back-btn"
        >
          {navBusy ? 'Going back…' : 'Back to Development'}
        </button>
        <button
          type="button"
          onClick={openConfirm}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors text-sm font-medium"
          data-testid="ccs-submit-btn"
        >
          Submit to Production
        </button>
      </div>

      {/* Submit confirmation */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="ccs-submit-confirm">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-5 py-3 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-white">Confirm submission</h4>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="text-slate-400 hover:text-white text-sm"
              >
                Close
              </button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <p className="text-xs text-slate-400">This routes exactly the content below to production.</p>

              <div>
                <span className="text-xs text-slate-500 block mb-1">Primary Hook</span>
                <p className="text-slate-200 whitespace-pre-wrap">{pHook || '—'}</p>
              </div>
              {altHooks.length > 0 && (
                <div>
                  <span className="text-xs text-slate-500 block mb-1">Alternate Hooks</span>
                  <ul className="list-disc pl-4 space-y-0.5 text-slate-300">
                    {altHooks.map((h) => <li key={h.artifact_id} className="whitespace-pre-wrap">{h.text || '—'}</li>)}
                  </ul>
                </div>
              )}
              <div>
                <span className="text-xs text-slate-500 block mb-1">Script Body</span>
                <p className="text-slate-200 whitespace-pre-wrap">{bodyText || '—'}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500 block mb-1">Primary CTA</span>
                <p className="text-slate-200 whitespace-pre-wrap">{pCta || '—'}</p>
              </div>
              {altCtas.length > 0 && (
                <div>
                  <span className="text-xs text-slate-500 block mb-1">Alternate CTAs</span>
                  <ul className="list-disc pl-4 space-y-0.5 text-slate-300">
                    {altCtas.map((c) => <li key={c.artifact_id} className="whitespace-pre-wrap">{c.text || '—'}</li>)}
                  </ul>
                </div>
              )}
              <div>
                <span className="text-xs text-slate-500 block mb-1">Creative Brief</span>
                <p className="text-slate-200 whitespace-pre-wrap">{brief.brief_text || '—'}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500 block mb-1">Production Instructions</span>
                <p className="text-slate-200 whitespace-pre-wrap">{brief.production_instructions || '—'}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500 block mb-1">Video Format</span>
                <p className="text-slate-200">{videoFormat}</p>
              </div>

              {submitError && (
                <p className="text-xs text-rose-400">{submitError}</p>
              )}
            </div>
            <div className="sticky bottom-0 bg-slate-900 border-t border-slate-800 px-5 py-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
                className="px-3 py-1.5 text-sm bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 disabled:opacity-40"
                data-testid="ccs-confirm-submit"
              >
                {submitting ? 'Submitting…' : (submitError ? 'Retry Submit' : 'Confirm & Submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy-to-field replace/append prompt */}
      {copyConflict && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="ccs-copy-conflict">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-sm w-full space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-white">{conflictLabel} already has content</h4>
              <p className="text-xs text-slate-400 mt-1">Replace it with the copied text, or append below?</p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCopyConflict(null)}
                className="px-3 py-1.5 text-sm bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
                data-testid="ccs-copy-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { applyCopy(copyConflict.key, copyConflict.text, 'append'); setCopyConflict(null); }}
                className="px-3 py-1.5 text-sm bg-slate-600 text-white rounded-lg hover:bg-slate-500"
                data-testid="ccs-copy-append"
              >
                Append
              </button>
              <button
                type="button"
                onClick={() => { applyCopy(copyConflict.key, copyConflict.text, 'replace'); setCopyConflict(null); }}
                className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-500"
                data-testid="ccs-copy-replace"
              >
                Replace
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
